import type { ReactNode } from 'react';
import { RouteLineOverlay } from '../RouteLineOverlay';
import { MIN_ROUTE_LINE_POINTS } from '../route-line';
import type { PhotoLineController } from './usePhotoLine';
import { ZoomResetButton } from './ZoomResetButton';

interface PhotoLineCanvasProps {
  editor: PhotoLineController;
  imageUrl: string;
  resetZoomLabel: string;
  testId?: string;
  className?: string;
  /** Drawn on the photo under the line, moving with it as it is zoomed. Must not take presses. */
  underlay?: ReactNode;
}

/** Green start, red anchor — the colours the holds and the published overlay use. */
const START_COLOR = '#22c55e';
const ANCHOR_COLOR = '#ef4444';
const MIDDLE_COLOR = '#3b82f6';

/**
 * The photo with the route's line on it.
 *
 * The line is drawn by the same overlay the published climb uses, so what is
 * being edited and what will be shown are one picture. Two departures, both
 * because this is the surface the corridor is *set* on: it darkens rather than
 * desaturates (a grey cliff greyed out looks like a grey cliff, and the slider
 * would appear to do nothing), and it leaves the end markers to the handles
 * below, which draw the same two points at the same size as every other one.
 */
export function PhotoLineCanvas({
  editor,
  imageUrl,
  resetZoomLabel,
  testId,
  className,
  underlay,
}: PhotoLineCanvasProps) {
  const { zoom, line, points, drawable, dragging, selected, naturalWidth, naturalHeight, frameProps } =
    editor;

  return (
    <div
      {...zoom.frameProps}
      onPointerDown={(event) => {
        zoom.frameProps.onPointerDown(event);
        frameProps.onPointerDown(event);
      }}
      onPointerMove={(event) => {
        zoom.frameProps.onPointerMove(event);
        frameProps.onPointerMove(event);
      }}
      onPointerUp={(event) => {
        zoom.frameProps.onPointerUp(event);
        frameProps.onPointerUp(event);
      }}
      onPointerCancel={(event) => {
        zoom.frameProps.onPointerCancel(event);
        frameProps.onPointerCancel(event);
      }}
      data-testid={testId}
      className={`relative w-full cursor-crosshair select-none overflow-hidden bg-muted ${className ?? ''}`}
    >
      <div ref={zoom.contentRef} {...zoom.layerProps}>
        <img src={imageUrl} alt="" className="block w-full" draggable={false} />

        {underlay}

        {/* The sweep is off: it would replay on every point placed. */}
        {drawable && (
          <RouteLineOverlay
            line={line}
            naturalWidth={naturalWidth}
            naturalHeight={naturalHeight}
            objectFit="contain"
            animate={false}
            focusStyle="dim"
            showEndMarkers={false}
          />
        )}

        {/* The vertices, which only the editor shows — they are what there is to
            grab, and a single point has no line to draw yet. Which one a press
            means is the frame's to work out; they are hit-testable only so a
            drag that starts on one is not read as the page being scrolled, which
            `touch-action` can only say from whatever the finger landed on. Their
            size is divided back out of the zoom so a handle stays a handle close
            up. */}
        {points.map((point, index) => (
          <span
            key={index}
            aria-hidden
            data-testid="photo-line-vertex"
            className={`absolute size-3 rounded-full border-2 shadow ${
              selected === index ? 'border-foreground' : 'border-white'
            }`}
            style={{
              left: `${point.x * 100}%`,
              top: `${point.y * 100}%`,
              transform: `translate(-50%, -50%) scale(${(dragging === index ? 1.25 : 1) / zoom.scale})`,
              touchAction: 'none',
              backgroundColor:
                index === 0
                  ? START_COLOR
                  : index === points.length - 1 && points.length >= MIN_ROUTE_LINE_POINTS
                    ? ANCHOR_COLOR
                    : MIDDLE_COLOR,
            }}
          />
        ))}
      </div>

      <ZoomResetButton zoom={zoom} label={resetZoomLabel} />
    </div>
  );
}
